// Anke, 2026-07-16 — das sichtbare Raster: Punkte an den Kreuzungen.
//
// Gezeichnet wird in die (sonst leere) Hintergrund-Ebene. Die Buehne traegt
// Verschiebung und Zoom, die Ebenen selbst nicht — ein in WELTKOORDINATEN
// gezeichnetes Raster wandert und zoomt darum von selbst mit dem Inhalt mit.
//
// EINE Konva-Form mit eigener Zeichenfunktion statt tausender Einzelpunkte:
// Konva ruft die Funktion bei jedem Neuzeichnen auf; sie rechnet den gerade
// sichtbaren Weltausschnitt aus und setzt nur dort Punkte. So bleibt die
// unendliche Flaeche bezahlbar — gefuellt wird immer nur das Fenster.

import Konva from 'konva';
import { GRID_STEP_WORLD } from './grid-snap.helper.js';

/** Punktfarbe — ein ruhiges, helles Grau, das den Inhalt nicht stoert. */
const DOT_COLOR = '#c3c9d2';

/** Punktradius in BILDSCHIRM-Pixeln, also zoomunabhaengig konstant. */
const DOT_RADIUS_PX = 1.1;

/**
 * Kleinster erlaubter Punktabstand auf dem Bildschirm. Beim starken
 * Herauszoomen ruecken die Punkte zusammen; sobald sie enger als das waeren,
 * wird nur noch JEDER ZWEITE gezeichnet (Abstand verdoppelt). Weil verdoppelt
 * wird, bleiben die gezeichneten Punkte eine Teilmenge des Grundrasters — die
 * Ausrichtung stimmt weiter, es wird nur ausgeduennt.
 */
const MIN_DOT_SPACING_PX = 16;

const TWO_PI = Math.PI * 2;

/**
 * Verwaltet die Raster-Form in der Hintergrund-Ebene.
 *
 * `enabled` steuert nur die Sichtbarkeit — das Einrasten haengt am selben
 * Zustand, wird aber anderswo gelesen (Board-Element). Hier geht es allein ums
 * Zeichnen.
 */
export class GridController {
  readonly #shape: Konva.Shape;
  #enabled = false;

  constructor(private readonly layer: Konva.Layer) {
    this.#shape = new Konva.Shape({
      listening: false,
      visible: false,
      sceneFunc: (ctx, shape) => this.#draw(ctx, shape),
    });
    this.layer.add(this.#shape);
  }

  /** Raster ein-/ausschalten. Zeichnet die Ebene neu. */
  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    this.#shape.visible(enabled);
    this.layer.batchDraw();
  }

  get enabled(): boolean {
    return this.#enabled;
  }

  /** Bei Verschiebung/Zoom/Groessenaenderung neu zeichnen. No-op, wenn aus. */
  redraw(): void {
    if (!this.#enabled) return;
    this.layer.batchDraw();
  }

  #draw(ctx: Konva.Context, shape: Konva.Shape): void {
    const stage = shape.getStage();
    if (!stage) return;
    const scale = stage.scaleX();
    if (scale <= 0) return;

    // Sichtbarer Weltausschnitt: Bildschirmecken zurueck in Weltkoordinaten.
    const wLeft = (0 - stage.x()) / scale;
    const wTop = (0 - stage.y()) / scale;
    const wRight = (stage.width() - stage.x()) / scale;
    const wBottom = (stage.height() - stage.y()) / scale;

    // Ausduennen: Abstand verdoppeln, bis die Punkte am Bildschirm weit genug
    // auseinanderliegen. Bleibt ein Vielfaches des Grundrasters.
    let step = GRID_STEP_WORLD;
    while (step * scale < MIN_DOT_SPACING_PX) step *= 2;

    // Punktradius konstant in Bildschirmpixeln: Die Zeichenfunktion laeuft im
    // bereits zoom-transformierten Kontext, also gegenskalieren.
    const r = DOT_RADIUS_PX / scale;

    const startX = Math.floor(wLeft / step) * step;
    const startY = Math.floor(wTop / step) * step;

    ctx.setAttr('fillStyle', DOT_COLOR);
    for (let x = startX; x <= wRight; x += step) {
      for (let y = startY; y <= wBottom; y += step) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TWO_PI, false);
        ctx.closePath();
        ctx.fill();
      }
    }
  }
}
